// Pure weather helpers for the localized rain-shower system.
//
// Showers spawn on terrain-weighted cells: mountains and forests draw
// rain, plains/hills get occasional showers, desert/arid tiles never do.
// One shower = cloud puffs + falling streaks over a 2–4 hex radius, with a
// fade-in → hold → fade-out life cycle.

import type { SceneNode } from '@aod/protocol';
import { buildBiomeMap, surfaceYAt } from './elevation.js';

/** Relative chance a biome hosts a shower. 0 = never. */
export const BIOME_RAIN_WEIGHT: Record<string, number> = {
  mountain: 3,
  forest: 2.5,
  hills: 1.2,
  tundra: 1,
  plains: 1,
  coast: 0.4,
  river: 0.4,
  ocean: 0,
  desert: 0,
};

/** Rain candidate: a tile's world anchor + its biome's rain weight. */
export interface RainCandidate {
  readonly x: number;
  readonly z: number;
  readonly baseY: number;
  readonly weight: number;
}

/** Weighted random pick (Math.random — cosmetic, never gameplay state). */
export function pickRainCandidate(
  candidates: readonly RainCandidate[],
  random: () => number = Math.random,
): RainCandidate | null {
  const total = candidates.reduce((n, c) => n + c.weight, 0);
  if (total <= 0) return null;
  let roll = random() * total;
  for (const c of candidates) {
    roll -= c.weight;
    if (roll <= 0) return c;
  }
  return candidates[candidates.length - 1] ?? null;
}

/** Build the weighted candidate list from the board tiles. */
export function buildRainCandidates(
  tiles: ReadonlyArray<SceneNode>,
  biomeMap?: Map<string, string>,
): RainCandidate[] {
  const map = biomeMap ?? buildBiomeMap(tiles);
  const out: RainCandidate[] = [];
  for (const tile of tiles) {
    const props = tile.props as Record<string, unknown> | undefined;
    const q = props?.q as number | undefined;
    const r = props?.r as number | undefined;
    if (q === undefined || r === undefined) continue;
    const biome = (props?.biome as string) ?? 'plains';
    const weight = BIOME_RAIN_WEIGHT[biome] ?? 0;
    if (weight <= 0) continue;
    const [x, , z] = tile.transform.position;
    out.push({ x, z, baseY: surfaceYAt(q, r, map, 0, 0), weight });
  }
  return out;
}

/**
 * Shower opacity over its life: fade in, hold, fade out. Times in seconds
 * from the shower's start.
 */
export function showerAlpha(elapsed: number, fadeIn = 4, hold = 22, fadeOut = 6): number {
  if (elapsed < 0 || elapsed >= fadeIn + hold + fadeOut) return 0;
  if (elapsed < fadeIn) return elapsed / fadeIn;
  if (elapsed < fadeIn + hold) return 1;
  return 1 - (elapsed - fadeIn - hold) / fadeOut;
}

/** Total shower duration for the given phases. */
export function showerDuration(fadeIn = 4, hold = 22, fadeOut = 6): number {
  return fadeIn + hold + fadeOut;
}
